import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { DayListGroup } from './day-list'
import { DayListGroups } from './day-list-groups'

/** What `?open=` holds for this sheet. */
export const REVIEWS_TO_LEAVE_OPEN = 'reviews-to-leave'

export type ReviewsToLeaveSheetState =
  /** The month is still on its way (first load, or paging): never "nothing owed". */
  | { status: 'pending' }
  /** The reviews layer was not read for this month (failed, or switched off): said, not guessed. */
  | { status: 'unavailable'; message: string }
  | { status: 'ok'; owedCount: number; groups: DayListGroup[] }

/**
 * The reviews you still owe, opened from the toolbar chip: the same rows as
 * the day list, soonest closing first. Read-only like the grid: each row opens
 * the stay sheet at its review section, and a link goes to that stay's row on
 * the Reviews page, where a person reads and sends the drafts.
 */
export function ReviewsToLeaveSheet({
  state,
  onClose,
}: {
  state: ReviewsToLeaveSheetState
  onClose: () => void
}) {
  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby="reviews-to-leave-description" data-testid="reviews-sheet">
        <SheetHeader>
          <SheetTitle>Reviews to leave</SheetTitle>
          <SheetDescription id="reviews-to-leave-description">
            {state.status !== 'ok'
              ? 'Calendar'
              : state.owedCount > 0
                ? `${state.owedCount} ${state.owedCount === 1 ? 'review' : 'reviews'} to leave, soonest to close first.`
                : 'No review is known to be owed.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          {state.status === 'pending' ? (
            <div className="space-y-3" aria-busy="true" data-testid="reviews-sheet-loading">
              <LoadingSkeleton className="h-6 w-1/2" />
              <LoadingSkeleton className="h-20 w-full" />
            </div>
          ) : state.status === 'unavailable' ? (
            <p className="text-sm text-textMuted" data-testid="reviews-sheet-unavailable">
              {state.message}
            </p>
          ) : state.groups.length === 0 ? (
            <p className="text-sm text-textMuted" data-testid="reviews-sheet-empty">
              Nothing to list: no review is known to be owed, and no thank-you is waiting, for a
              stay in this month.
            </p>
          ) : (
            <DayListGroups groups={state.groups} />
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
